import React, { useMemo, useState } from 'react';
import {
  FaCheckCircle,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaSun,
  FaUsers
} from 'react-icons/fa';
import { createSummerSportsRegistration } from '../../../api/sportsAppointmentService';
import './SummerSportsRegistrationForm.scss';

const branchBlocks = [
  {
    facilityName: 'Tesis Sonra Belirlenecektir',
    branches: ['Taekwondo', 'Kick-Boks']
  },
  {
    facilityName: 'Millet Parkı Spor Tesisi (Fener Cad.)',
    branches: ['Futbol', 'Voleybol', 'Basketbol', 'Tenis']
  },
  {
    facilityName: 'Mustafa Kemal Kapalı Spor Salonu',
    branches: ['Voleybol', 'Jimnastik', 'Basketbol', 'Çocuk Zumbası']
  },
  {
    facilityName: 'Denizciler Spor Tesisi',
    branches: ['Basketbol', 'Voleybol', 'Hentbol', 'Futbol']
  },
  {
    facilityName: 'Ziraat Park Sosyal Tesisi',
    branches: ['Basketbol', 'Voleybol']
  },
  {
    facilityName: 'Yeni Millet Bahçesi Spor Tesisi',
    branches: ['Futbol', 'Voleybol', 'Basketbol', 'Tenis']
  },
  {
    facilityName: 'Kapalı Yüzme Havuzu',
    branches: ['Yüzme']
  },
  {
    facilityName: 'Karayılan Spor Tesisi',
    branches: ['Futbol']
  }
];

const guardianRelationshipOptions = ['Anne', 'Baba', 'Vasi', 'Diğer'];

const initialFormData = {
  childFirstName: '',
  childLastName: '',
  childIdentityNumber: '',
  childBirthDate: '',
  guardianFirstName: '',
  guardianLastName: '',
  guardianIdentityNumber: '',
  guardianPhone: '',
  guardianRelationship: '',
  address: '',
  consentApproved: false
};

const normalizeDigits = (value = '') => value.replace(/\D/g, '');

const toInputDate = (date) => {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getAgeFromBirthDate = (birthDate) => {
  if (!birthDate) return null;

  const today = new Date();
  const date = new Date(birthDate);
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
    age -= 1;
  }

  return age;
};

const isValidTckn = (value) => /^\d{11}$/.test(value);

const SummerSportsRegistrationForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successSummary, setSuccessSummary] = useState(null);

  const childAge = useMemo(
    () => getAgeFromBirthDate(formData.childBirthDate),
    [formData.childBirthDate]
  );

  const birthDateLimits = useMemo(() => {
    const today = new Date();
    const minDate = new Date(today);
    const maxDate = new Date(today);

    minDate.setFullYear(today.getFullYear() - 14);
    maxDate.setFullYear(today.getFullYear() - 6);

    return {
      min: toInputDate(minDate),
      max: toInputDate(maxDate)
    };
  }, []);



  const handleValueChange = (field) => (event) => {
    const { value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [field]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDigitsChange = (field, maxLength) => (event) => {
    const digits = normalizeDigits(event.target.value).slice(0, maxLength);
    setFormData((prev) => ({
      ...prev,
      [field]: digits
    }));
  };

  const isBranchSelected = (facilityName, branchName) =>
    selectedBranches.some(
      (item) => item.facilityName === facilityName && item.branchName === branchName
    );

  const toggleBranchSelection = (facilityName, branchName) => {
    setSelectedBranches((prev) => {
      const exists = prev.some(
        (item) => item.facilityName === facilityName && item.branchName === branchName
      );

      if (exists) {
        return prev.filter(
          (item) => !(item.facilityName === facilityName && item.branchName === branchName)
        );
      }

      return [...prev, { facilityName, branchName }];
    });
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedBranches([]);
    setError('');
  };

  const validateForm = () => {
    if (!formData.childFirstName.trim() || !formData.childLastName.trim()) {
      return 'Çocuğun ad ve soyad bilgisi zorunludur.';
    }

    if (!isValidTckn(formData.childIdentityNumber)) {
      return 'Çocuğun T.C. kimlik numarası 11 haneli ve geçerli olmalıdır.';
    }

    if (!formData.childBirthDate) {
      return 'Doğum tarihi zorunludur.';
    }

    if (childAge === null || childAge < 6 || childAge > 14) {
      return 'Yaz Kursları başvurusu sadece 6-14 yaş aralığındaki çocuklar için alınmaktadır.';
    }

    if (!formData.guardianFirstName.trim() || !formData.guardianLastName.trim()) {
      return 'Veli ad ve soyad bilgisi zorunludur.';
    }

    if (!formData.guardianRelationship) {
      return 'Veli yakınlık bilgisi seçilmelidir.';
    }

    if (!isValidTckn(formData.guardianIdentityNumber)) {
      return 'Velinin T.C. kimlik numarası 11 haneli ve geçerli olmalıdır.';
    }

    if (!/^\d{10,11}$/.test(formData.guardianPhone)) {
      return 'Telefon numarası 10 veya 11 haneli olmalıdır.';
    }

    if (!formData.address.trim()) {
      return 'İkametgah adresi zorunludur.';
    }

    if (selectedBranches.length === 0) {
      return 'Lütfen en az bir tesis / branş tercihi seçiniz.';
    }

    if (!formData.consentApproved) {
      return 'Veli onayı olmadan başvuru gönderilemez.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const validationMessage = validateForm();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    const payload = {
      childFirstName: formData.childFirstName.trim(),
      childLastName: formData.childLastName.trim(),
      childIdentityNumber: formData.childIdentityNumber,
      childBirthDate: formData.childBirthDate,
      guardianFirstName: formData.guardianFirstName.trim(),
      guardianLastName: formData.guardianLastName.trim(),
      guardianIdentityNumber: formData.guardianIdentityNumber,
      guardianPhone: formData.guardianPhone,
      guardianRelationship: formData.guardianRelationship,
      address: formData.address.trim(),
      consentApproved: formData.consentApproved,
      selectedBranches: selectedBranches.map((item) => ({
        facilityName: item.facilityName,
        branchName: item.branchName
      }))
    };

    setSubmitting(true);
    try {
      const response = await createSummerSportsRegistration(payload);
      if (!response.isSuccess) {
        setError(response.resultMessage || 'Başvuru kaydedilemedi.');
        return;
      }

      setSuccessSummary({
        childName: `${payload.childFirstName} ${payload.childLastName}`,
        guardianName: `${payload.guardianFirstName} ${payload.guardianLastName}`,
        guardianPhone: payload.guardianPhone,
        selectedBranches: payload.selectedBranches
      });
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sa-panel-inner sa-panel-inner--wide sa-unified-mode sa-summer-sports">
      <div className="sa-facility-details">
        <div className="sa-facility-details-header sa-unified-tab-header sa-summer-header-simple">
          <div className="sa-unified-tab-header-row">
            <h3 className="sa-unified-tab-title">
              <div className="sa-unified-tab-icon">
                <FaSun size={20} />
              </div>
              Yaz Spor Kursları Kayıt
            </h3>
          </div>
          <p className="sa-unified-tab-subtitle">
            Yaz spor kursu başvurusu için çocuk, veli ve tesis / branş tercih bilgilerini giriniz.
          </p>
        </div>

        {successSummary ? (
          <div className="sa-appointment-section sa-summer-section">
            <div className="sa-payment-result sa-payment-success sa-summer-success">
              <div className="sa-result-icon">
                <FaCheckCircle />
              </div>
              <h2>Başvurunuz Alındı</h2>
              <p className="sa-result-message">
                Yaz Kursları başvurunuz başarıyla kaydedildi. Kayıt, panelde Yaz Kursları tablosunda görünecektir.
              </p>

              <div className="sa-summer-success-grid">
                <div>
                  <span>Çocuk</span>
                  <strong>{successSummary.childName}</strong>
                </div>
                <div>
                  <span>Veli</span>
                  <strong>{successSummary.guardianName}</strong>
                </div>
                <div>
                  <span>Telefon</span>
                  <strong>{successSummary.guardianPhone}</strong>
                </div>
                <div>
                  <span>Seçim</span>
                  <strong>{successSummary.selectedBranches.length} branş</strong>
                </div>
              </div>

              <div className="sa-summer-chip-grid">
                {successSummary.selectedBranches.map((selection) => (
                  <span
                    key={`${selection.facilityName}-${selection.branchName}`}
                    className="sa-summer-chip"
                  >
                    {selection.facilityName} / {selection.branchName}
                  </span>
                ))}
              </div>

              <div className="sa-action-row sa-public-action-row sa-summer-action-row">
                <button
                  type="button"
                  className="sa-btn-cancel"
                  onClick={() => setSuccessSummary(null)}
                >
                  Yeni Başvuru
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form className="sa-appointment-section sa-summer-section" onSubmit={handleSubmit}>
            <div className="sa-booking-form sa-summer-card">
              <div className="sa-summer-section-title">
                <span className="sa-summer-section-icon">
                  <FaIdCard />
                </span>
                Çocuk Bilgileri
              </div>
              <div className="sa-form-grid">
                <div className="sa-form-group">
                  <label>Ad <span className="sa-required">*</span></label>
                  <input
                    className="sa-form-input"
                    value={formData.childFirstName}
                    onChange={handleValueChange('childFirstName')}
                    maxLength={50}
                    required
                  />
                </div>
                <div className="sa-form-group">
                  <label>Soyad <span className="sa-required">*</span></label>
                  <input
                    className="sa-form-input"
                    value={formData.childLastName}
                    onChange={handleValueChange('childLastName')}
                    maxLength={50}
                    required
                  />
                </div>
                <div className="sa-form-group">
                  <label>T.C. Kimlik No <span className="sa-required">*</span></label>
                  <input
                    className="sa-form-input"
                    inputMode="numeric"
                    placeholder="11 haneli"
                    value={formData.childIdentityNumber}
                    onChange={handleDigitsChange('childIdentityNumber', 11)}
                    pattern="\d{11}"
                    minLength={11}
                    maxLength={11}
                    title="11 haneli T.C. kimlik numarası giriniz."
                    autoComplete="off"
                    required
                  />
                  <small className="sa-summer-help">
                    11 haneli ve yalnızca rakamlardan oluşmalıdır.
                  </small>
                </div>
                <div className="sa-form-group">
                  <label>Doğum Tarihi <span className="sa-required">*</span></label>
                  <input
                    className="sa-form-input"
                    type="date"
                    value={formData.childBirthDate}
                    onChange={handleValueChange('childBirthDate')}
                    min={birthDateLimits.min}
                    max={birthDateLimits.max}
                    required
                  />
                  <small className="sa-summer-help">
                    {childAge === null
                      ? 'Yaş aralığı: 6-14'
                      : `Hesaplanan yaş: ${childAge}`}
                  </small>
                </div>
              </div>
            </div>

            <div className="sa-booking-form sa-summer-card">
              <div className="sa-summer-section-title">
                <span className="sa-summer-section-icon">
                  <FaUsers />
                </span>
                Veli Bilgileri
              </div>
              <div className="sa-form-grid">
                <div className="sa-form-group">
                  <label>Veli Adı <span className="sa-required">*</span></label>
                  <input
                    className="sa-form-input"
                    value={formData.guardianFirstName}
                    onChange={handleValueChange('guardianFirstName')}
                    maxLength={50}
                    required
                  />
                </div>
                <div className="sa-form-group">
                  <label>Veli Soyadı <span className="sa-required">*</span></label>
                  <input
                    className="sa-form-input"
                    value={formData.guardianLastName}
                    onChange={handleValueChange('guardianLastName')}
                    maxLength={50}
                    required
                  />
                </div>
                <div className="sa-form-group">
                  <label>Yakınlık <span className="sa-required">*</span></label>
                  <select
                    className="sa-form-input"
                    value={formData.guardianRelationship}
                    onChange={handleValueChange('guardianRelationship')}
                    required
                  >
                    <option value="">Seçiniz</option>
                    {guardianRelationshipOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sa-form-group">
                  <label>Veli T.C. Kimlik No <span className="sa-required">*</span></label>
                  <input
                    className="sa-form-input"
                    inputMode="numeric"
                    placeholder="11 haneli"
                    value={formData.guardianIdentityNumber}
                    onChange={handleDigitsChange('guardianIdentityNumber', 11)}
                    pattern="\d{11}"
                    minLength={11}
                    maxLength={11}
                    title="11 haneli T.C. kimlik numarası giriniz."
                    autoComplete="off"
                    required
                  />
                  <small className="sa-summer-help">
                    11 haneli ve yalnızca rakamlardan oluşmalıdır.
                  </small>
                </div>
                <div className="sa-form-group">
                  <label>Telefon <span className="sa-required">*</span></label>
                  <input
                    className="sa-form-input"
                    inputMode="numeric"
                    placeholder="05XXXXXXXXX"
                    value={formData.guardianPhone}
                    onChange={handleDigitsChange('guardianPhone', 11)}
                    maxLength={11}
                    required
                  />
                  <small className="sa-summer-help">
                    Telefon numaranızı giriniz.
                  </small>
                </div>
                <div className="sa-form-group sa-form-group--full">
                  <label>İkametgah <span className="sa-required">*</span></label>
                  <textarea
                    className="sa-form-input sa-summer-textarea"
                    value={formData.address}
                    onChange={handleValueChange('address')}
                    rows={3}
                    maxLength={500}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="sa-booking-form sa-summer-card">
              <div className="sa-summer-section-title">
                <span className="sa-summer-section-icon">
                  <FaMapMarkerAlt />
                </span>
                Tesis / Branş Seçimi
              </div>
              <p className="sa-summer-selection-copy" style={{ marginBottom: '16px' }}>
                Birden fazla tesis ve branş seçebilirsiniz.
              </p>

              <div className="sa-summer-branch-grid">
                {branchBlocks.map((block) => (
                  <div
                    key={block.facilityName}
                    className="sa-summer-branch-card"
                  >
                    <div className="sa-summer-branch-card-header">
                      <h4>{block.facilityName}</h4>
                      <span>{block.branches.length} seçenek</span>
                    </div>
                    <div className="sa-summer-branch-actions">
                      {block.branches.map((branchName) => {
                        const selected = isBranchSelected(block.facilityName, branchName);
                        return (
                          <button
                            key={branchName}
                            type="button"
                            className={`sa-summer-branch-option ${selected ? 'is-selected' : ''}`}
                            onClick={() => toggleBranchSelection(block.facilityName, branchName)}
                          >
                            <span className="sa-summer-checkmark" aria-hidden="true">
                              {selected ? '✓' : ''}
                            </span>
                            <span>{branchName}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sa-booking-form sa-summer-card sa-summer-card--compact">
              <div className="sa-summer-section-title">
                <span className="sa-summer-section-icon">
                  <FaPhoneAlt />
                </span>
                Onay
              </div>
              <label className="sa-summer-consent">
                <input
                  type="checkbox"
                  checked={formData.consentApproved}
                  onChange={handleValueChange('consentApproved')}
                />
                <span>
                  Çocuğumun spor yapmasına engel olacak ciddi bir sağlık problemi bulunmadığını beyan ediyor,
                  seçmiş olduğum Yaz Kursları programına katılmasına izin veriyorum.
                </span>
              </label>

              {error && <div className="sa-public-message sa-public-message--error">{error}</div>}

              <div className="sa-action-row sa-public-action-row sa-summer-action-row">
                <button type="submit" className="sa-submit-button" disabled={submitting}>
                  <FaSun />
                  {submitting ? 'Başvuru Gönderiliyor...' : 'Yaz Kursları Formunu Gönder'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SummerSportsRegistrationForm;
